import { useMemo, useState } from 'react'
import { queryAI } from '@/api/ai'
import { AiAnswer } from '@/components/ai/AiAnswer'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Skeleton } from '@/components/ui/skeleton'
import {
  EVENT_LOG_SLUG,
  EVENT_LOG_TAB_DESCRIPTION,
  describeEventFilter,
  eventFilterKey,
  eventLogSnapshotFromRmmData,
  getSummaryCountForFilter,
  groupEventsByDate,
  isSameEventFilter,
  levelBadgeVariant,
  selectEventsForFilter,
  snapshotNeedsResample,
} from '@/lib/eventLogHealth'
import { formatDateOnly, formatDateTime } from '@/lib/formatAgent'
import {
  assessmentChecks,
  checkStatusLabel,
  checkStatusVariant,
  normaliseToolkitStatus,
  toolkitStatusLabel,
} from '@/lib/toolkitHealth'
import { cn } from '@/lib/utils'

const LEVEL_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'error', label: 'Error' },
  { id: 'warning', label: 'Warning' },
]

const DEFAULT_EVENT_FILTER = {
  level: 'all',
  logName: null,
  pattern: null,
  provider: null,
}

function statusPillVariant(status) {
  const normalised = normaliseToolkitStatus(status)
  if (normalised === 'PASS') return 'ready'
  if (normalised === 'WARN') return 'pending'
  if (normalised === 'FAIL') return 'destructive'
  return 'inactive'
}

function CountTile({ label, value, variant, active, onClick }) {
  const clickable = Boolean(onClick) && (value ?? 0) > 0
  const Tag = clickable ? 'button' : 'div'

  return (
    <Tag
      type={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      className={cn(
        'rounded-md border border-border px-3 py-2 text-left transition-colors',
        clickable && 'cursor-pointer hover:border-primary/60 hover:bg-muted/40',
        active && 'border-primary bg-primary/5 ring-1 ring-primary/20',
        !clickable && 'opacity-90',
      )}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      {variant ? (
        <Badge variant={variant} className="mt-1">{value ?? 0}</Badge>
      ) : (
        <p className="mt-1 text-lg font-semibold tabular-nums">{value ?? 0}</p>
      )}
      {clickable && (
        <p className="mt-1 text-[11px] text-muted-foreground">Click to filter events</p>
      )}
    </Tag>
  )
}

function LogSummaryGrid({ title, logName, summary, eventFilter, onFilterSelect }) {
  if (!summary) return null

  function tileActive(level) {
    return (
      eventFilter.logName === logName &&
      eventFilter.level === level &&
      !eventFilter.pattern &&
      !eventFilter.provider
    )
  }

  function selectLevel(level) {
    const next = { level, logName, pattern: null, provider: null }
    if (isSameEventFilter(eventFilter, next)) {
      onFilterSelect(DEFAULT_EVENT_FILTER)
      return
    }
    onFilterSelect(next)
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{title}</p>
      {summary.access_error && (
        <p className="text-sm text-destructive">{summary.access_error}</p>
      )}
      <div className="grid gap-2 sm:grid-cols-3">
        <CountTile
          label="Critical"
          value={summary.critical}
          variant="destructive"
          active={tileActive('critical')}
          onClick={() => selectLevel('critical')}
        />
        <CountTile
          label="Error"
          value={summary.error}
          variant="destructive"
          active={tileActive('error')}
          onClick={() => selectLevel('error')}
        />
        <CountTile
          label="Warning"
          value={summary.warning}
          variant="warning"
          active={tileActive('warning')}
          onClick={() => selectLevel('warning')}
        />
      </div>
      {summary.limited && (
        <p className="text-xs text-muted-foreground">
          Event sample was limited; counts may be truncated.
        </p>
      )}
    </div>
  )
}

function ProviderTable({ title, providers, eventFilter, onFilterSelect }) {
  const rows = Array.isArray(providers) ? providers : []
  if (rows.length === 0) return null

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{title}</p>
      <div className="rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-medium">Provider</th>
              <th className="px-3 py-2 font-medium text-right">Count</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const active =
                eventFilter.provider === row.name &&
                !eventFilter.pattern &&
                eventFilter.level === 'all' &&
                !eventFilter.logName
              return (
                <tr key={row.name} className="border-b border-border last:border-0">
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => {
                        const next = {
                          level: 'all',
                          logName: null,
                          pattern: null,
                          provider: row.name,
                        }
                        onFilterSelect(isSameEventFilter(eventFilter, next) ? DEFAULT_EVENT_FILTER : next)
                      }}
                      className={cn(
                        'text-left hover:text-primary hover:underline',
                        active && 'font-semibold text-primary',
                      )}
                    >
                      {row.name || '—'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.count ?? '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ChecksTable({ checks }) {
  const rows = Array.isArray(checks) ? checks : []
  if (rows.length === 0) return null

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">Checks</p>
      <div className="rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-medium">Area</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((check, index) => (
              <tr key={`${check.area}-${index}`} className="border-b border-border last:border-0 align-top">
                <td className="px-3 py-2 font-medium">{check.area || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Badge variant={checkStatusVariant(check.status)}>
                    {checkStatusLabel(check.status)}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{check.detail || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function EventsByDate({ events, eventFilter, payload }) {
  const filtered = useMemo(
    () => (payload ? selectEventsForFilter(payload, eventFilter) : events ?? []),
    [events, eventFilter, payload],
  )
  const grouped = useMemo(() => groupEventsByDate(filtered), [filtered])
  const summaryCount = payload ? getSummaryCountForFilter(payload, eventFilter) : null
  const sampleCapped =
    summaryCount != null && filtered.length > 0 && filtered.length < summaryCount

  if (grouped.length === 0) {
    const needsResample = snapshotNeedsResample(payload)
    return (
      <p className="text-sm text-muted-foreground">
        No matching events in this snapshot.
        {needsResample
          ? ' Re-run the event-log-health script (v1.0.3 or later) on this device — counts come from the full log query, but the event list needs a fresh snapshot with per-category samples.'
          : ' The device may have no events in this category.'}
      </p>
    )
  }

  return (
    <div className="space-y-4">
      {sampleCapped && (
        <p className="text-xs text-muted-foreground">
          Showing {filtered.length} sampled events. Total in the last 24 hours:{' '}
          {summaryCount.toLocaleString('en-GB')}.
        </p>
      )}
      {grouped.map((group) => (
        <div key={group.date}>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            {formatDateOnly(group.date)}
          </p>
          <div className="rounded-md border border-border divide-y divide-border">
            {group.events.map((event, index) => (
              <div key={`${event.occurred_at}-${event.event_id}-${index}`} className="px-3 py-3 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={levelBadgeVariant(event.level)}>{event.level || '—'}</Badge>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {formatDateTime(event.occurred_at)}
                  </span>
                  <Badge variant="secondary">{event.log_name || '—'}</Badge>
                  <span className="text-xs text-muted-foreground">
                    ID {event.event_id ?? '—'}
                  </span>
                </div>
                <p className="text-sm font-medium">{event.provider || '—'}</p>
                <p className="text-sm text-muted-foreground break-words">{event.message || '—'}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function EventLogAnalysis({ agentId }) {
  const [analysing, setAnalysing] = useState(false)
  const [answer, setAnswer] = useState('')
  const [error, setError] = useState('')

  async function handleAnalyse() {
    if (!agentId || analysing) return
    setAnalysing(true)
    setError('')
    try {
      const result = await queryAI({
        question: [
          'Analyse the stored Windows event-log snapshot for this device.',
          'Explain what appears to be going wrong, prioritise the material findings,',
          'and give practical checks and suggested fixes for an MSP technician.',
        ].join(' '),
        scope: 'device_event_logs',
        agent_id: Number(agentId),
      })
      setAnswer(result.answer || '')
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('The analysis timed out. Please try again.')
      } else {
        setError(
          err.response?.data?.detail
          || 'Could not analyse these event logs. Check the connection and try again.',
        )
      }
    } finally {
      setAnalysing(false)
    }
  }

  return (
    <Card>
      <CardContent className="space-y-3 pt-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-medium">AI event-log analysis</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Runs only when you select Analyse and uses only the event-log snapshot stored in
              Bekat Dash. It does not query the device live.
            </p>
          </div>
          <Button type="button" onClick={handleAnalyse} disabled={!agentId || analysing}>
            {analysing ? 'Analysing…' : answer ? 'Analyse again' : 'Analyse'}
          </Button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {answer && <AiAnswer answer={answer} />}
      </CardContent>
    </Card>
  )
}

export function AgentEventLogs({ loading, data, agentId }) {
  const [openSections, setOpenSections] = useState({
    summary: false,
    patterns: false,
    providers: false,
    events: false,
    checks: false,
  })
  const [eventFilter, setEventFilter] = useState(DEFAULT_EVENT_FILTER)

  const snapshot = eventLogSnapshotFromRmmData(data)
  const payload = snapshot?.payload ?? null
  const checks = assessmentChecks(payload)
  const filteredEvents = useMemo(
    () => selectEventsForFilter(payload, eventFilter),
    [payload, eventFilter],
  )
  const hasActiveFilter = eventFilterKey(eventFilter) !== eventFilterKey(DEFAULT_EVENT_FILTER)
  const needsResample = snapshotNeedsResample(payload)

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!snapshot) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{EVENT_LOG_TAB_DESCRIPTION}</p>
        <Card className="border-dashed">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No event log data for this device yet. Deploy the {EVENT_LOG_SLUG} RMM script
            from Atera (run as System) to populate this tab.
          </CardContent>
        </Card>
      </div>
    )
  }

  const patterns = payload?.patterns ?? {}
  const collapsedHeader = [
    payload?.interpretation,
    payload?.script_version ? `Script v${payload.script_version}` : null,
    snapshot.collected_at ? `Collected ${formatDateTime(snapshot.collected_at)}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  function setSectionOpen(key, open) {
    setOpenSections((prev) => ({ ...prev, [key]: open }))
  }

  function applyEventFilter(next) {
    setEventFilter(next)
    setSectionOpen('events', true)
  }

  function selectPattern(pattern) {
    const next = { level: 'all', logName: null, pattern, provider: null }
    applyEventFilter(isSameEventFilter(eventFilter, next) ? DEFAULT_EVENT_FILTER : next)
  }

  function selectLevel(level) {
    applyEventFilter({
      ...eventFilter,
      level,
      pattern: null,
      provider: null,
    })
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{EVENT_LOG_TAB_DESCRIPTION}</p>
      <EventLogAnalysis agentId={agentId} />

      {needsResample && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="pt-4 text-sm text-muted-foreground">
            This snapshot is missing per-category event samples. Summary counts are correct,
            but filters may show no events until you re-run <span className="font-medium">event-log-health</span>{' '}
            v1.0.3 (or later) on the device in Atera.
          </CardContent>
        </Card>
      )}

      <CollapsePanel
        id="event-log-overview"
        title="Event log health"
        collapsedSummary={collapsedHeader}
        open={openSections.summary}
        onOpenChange={(open) => setSectionOpen('summary', open)}
        pill={payload?.overall_status ? toolkitStatusLabel(payload.overall_status) : undefined}
        pillVariant={statusPillVariant(payload?.overall_status)}
      >
        <p className="mb-4 text-xs text-muted-foreground">
          {payload?.script_version ? (
            <span>Script version {payload.script_version}</span>
          ) : (
            <span>Script version not reported (re-run event-log-health v1.0.3 or later)</span>
          )}
          {snapshot.collected_at && (
            <span>{' · '}Collected {formatDateTime(snapshot.collected_at)}</span>
          )}
        </p>
        {payload?.interpretation && (
          <p className="text-sm text-muted-foreground mb-4">{payload.interpretation}</p>
        )}
        <div className="grid gap-4 lg:grid-cols-2">
          <LogSummaryGrid
            title="System log (24 hours)"
            logName="System"
            summary={payload?.system}
            eventFilter={eventFilter}
            onFilterSelect={applyEventFilter}
          />
          <LogSummaryGrid
            title="Application log (24 hours)"
            logName="Application"
            summary={payload?.application}
            eventFilter={eventFilter}
            onFilterSelect={applyEventFilter}
          />
        </div>
      </CollapsePanel>

      <CollapsePanel
        id="event-log-patterns"
        title="Fault patterns (7 days)"
        collapsedSummary={[
          `${patterns.unexpected_shutdowns ?? 0} unexpected shutdowns`,
          `${patterns.disk_storage_errors ?? 0} disk errors (24h window)`,
          `${patterns.service_control_failures ?? 0} service failures (24h window)`,
        ].join(' · ')}
        open={openSections.patterns}
        onOpenChange={(open) => setSectionOpen('patterns', open)}
      >
        <div className="grid gap-2 sm:grid-cols-3">
          <CountTile
            label="Unexpected shutdowns (Kernel-Power 41)"
            value={patterns.unexpected_shutdowns}
            variant={patterns.unexpected_shutdowns >= 3 ? 'destructive' : patterns.unexpected_shutdowns > 0 ? 'warning' : 'success'}
            active={eventFilter.pattern === 'shutdown'}
            onClick={() => selectPattern('shutdown')}
          />
          <CountTile
            label="Disk / storage errors (24h)"
            value={patterns.disk_storage_errors}
            variant={patterns.disk_storage_errors >= 5 ? 'destructive' : patterns.disk_storage_errors > 0 ? 'warning' : 'success'}
            active={eventFilter.pattern === 'disk'}
            onClick={() => selectPattern('disk')}
          />
          <CountTile
            label="Service control failures (24h)"
            value={patterns.service_control_failures}
            variant={patterns.service_control_failures >= 10 ? 'destructive' : patterns.service_control_failures > 0 ? 'warning' : 'success'}
            active={eventFilter.pattern === 'service'}
            onClick={() => selectPattern('service')}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Shutdown pattern counts cover 7 days; the event list below is from the last 24 hours only.
        </p>
      </CollapsePanel>

      <CollapsePanel
        id="event-log-providers"
        title="Top error providers (24 hours)"
        collapsedSummary="Expand to view top System and Application providers"
        open={openSections.providers}
        onOpenChange={(open) => setSectionOpen('providers', open)}
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <ProviderTable
            title="System"
            providers={payload?.top_system_providers}
            eventFilter={eventFilter}
            onFilterSelect={applyEventFilter}
          />
          <ProviderTable
            title="Application"
            providers={payload?.top_application_providers}
            eventFilter={eventFilter}
            onFilterSelect={applyEventFilter}
          />
        </div>
      </CollapsePanel>

      <CollapsePanel
        id="event-log-events"
        title="Recent events (24 hours)"
        collapsedSummary={`${filteredEvents.length} of ${payload?.recent_events?.length ?? 0} events shown`}
        open={openSections.events}
        onOpenChange={(open) => setSectionOpen('events', open)}
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="inline-flex rounded-md border border-border p-0.5">
            {LEVEL_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => selectLevel(filter.id)}
                className={cn(
                  'rounded px-3 py-1 text-xs font-medium transition-colors',
                  eventFilter.level === filter.id && !eventFilter.pattern && !eventFilter.provider
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {hasActiveFilter && (
            <button
              type="button"
              onClick={() => applyEventFilter(DEFAULT_EVENT_FILTER)}
              className="text-xs text-primary hover:underline"
            >
              Clear filter
            </button>
          )}
        </div>
        {hasActiveFilter && (
          <p className="mb-3 text-sm text-muted-foreground">
            Showing: <span className="font-medium text-foreground">{describeEventFilter(eventFilter)}</span>
            {' · '}
            {filteredEvents.length} event{filteredEvents.length === 1 ? '' : 's'}
          </p>
        )}
        <EventsByDate payload={payload} eventFilter={eventFilter} />
      </CollapsePanel>

      <CollapsePanel
        id="event-log-checks"
        title="Full check list"
        collapsedSummary={`${checks.length} checks`}
        open={openSections.checks}
        onOpenChange={(open) => setSectionOpen('checks', open)}
      >
        <ChecksTable checks={checks} />
      </CollapsePanel>
    </div>
  )
}
